import {
  Button,
  Caption,
  Card,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  Group,
  Icon,
  ListCard,
  SectionHeader,
  Stack,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
} from "@assetwatch/ui";
import { Link } from "@tanstack/react-router";
import type { AssetTreeSearch } from "../../../lib/assetTreeSearch";
import type { AssetId } from "../../../lib/workIds";
import type { WorkSearch } from "../../../lib/workSearch";
import type { AssetDetailData } from "../../../queries/useAssetDetailQuery";
import { CopyAssetLinkButton } from "../../assets/CopyAssetLinkButton";
import { ASSET_KIND_LABEL } from "./assetModel";

export type AssetDetailSubAsset = {
  assetId: AssetId;
  code: string;
  name: string;
};

export type AssetDetailSideSheetProps = {
  assetId: AssetId | null;
  onOpenChange: (open: boolean) => void;
  assetName?: string | null;
  assetCode?: string | null;
  assetKind?: "ASSET" | "LOCATION" | null;
  assetDetailSearch?: AssetTreeSearch & WorkSearch;
  physicalLocation?: { name: string; code: string } | null;
  subAssets?: AssetDetailSubAsset[];
  detail?: AssetDetailData;
  isDetailLoading?: boolean;
  onSelectSubAsset?: (assetId: AssetId) => void;
  onStartWorkOrder?: () => void;
};

function displayDate(value?: string) {
  if (!value) return "Unavailable";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <Group align="center" justify="between" gap={12}>
      <Text size="sm" tone="subtle">
        {label}
      </Text>
      <span className="tabular-nums">
        <Text align="end" size="sm" weight="semibold">
          {value}
        </Text>
      </span>
    </Group>
  );
}

export function AssetDetailSideSheet({
  assetId,
  onOpenChange,
  assetName,
  assetCode,
  assetKind,
  assetDetailSearch,
  physicalLocation,
  subAssets = [],
  detail,
  isDetailLoading = false,
  onSelectSubAsset,
  onStartWorkOrder,
}: AssetDetailSideSheetProps) {
  const children =
    detail?.relationships.filter((item) => item.direction === "child") ?? [];
  const parents =
    detail?.relationships.filter((item) => item.direction === "parent") ?? [];
  const location =
    detail?.locationName ?? physicalLocation?.name ?? "Unavailable";
  const title = detail?.name ?? assetName ?? "Asset details";
  const code = detail?.code ?? assetCode ?? "";
  const kind = detail?.kind ?? assetKind;
  const search = assetDetailSearch ?? {};

  return (
    <Drawer open={assetId !== null} onOpenChange={onOpenChange} side="right">
      <DrawerContent size="small">
        <DrawerHeader
          title={title}
          subtitle={code}
          closeLabel="Close asset details"
          actions={
            <>
              {assetId ? (
                <Button
                  nativeButton={false}
                  render={
                    <Link
                      params={{ assetId }}
                      search={search}
                      to="/assets/$assetId"
                    />
                  }
                  icon="arrow-up-right"
                  variant="ghost"
                >
                  Open full view
                </Button>
              ) : null}
              <CopyAssetLinkButton resetKey={assetId} size="md" />
            </>
          }
        />
        <Tabs
          defaultValue="details"
          key={assetId ?? "closed"}
          render={<div className="flex min-h-0 flex-1 flex-col" />}
        >
          <TabsList inset>
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>
          <DrawerBody>
            <TabsContent value="details">
              <div className="pt-(--spacing-20)">
                <Stack gap={24}>
                  <Card key={assetId ?? "closed"} padding="comfortable">
                    <Stack gap={8}>
                      <DetailRow label="Location" value={location} />
                      <DetailRow
                        label="Kind"
                        value={kind ? ASSET_KIND_LABEL[kind] : "Unavailable"}
                      />
                      <details>
                        <summary className="cursor-pointer list-none">
                          <DetailRow label="More info" value="Show" />
                        </summary>
                        <Stack gap={8}>
                          <DetailRow
                            label="Created on"
                            value={displayDate(detail?.createdAt)}
                          />
                          <DetailRow
                            label="Created by"
                            value={detail?.createdBy ?? "Unavailable"}
                          />
                          <DetailRow
                            label="Last updated"
                            value={displayDate(detail?.updatedAt)}
                          />
                          <DetailRow
                            label="Last updated by"
                            value={detail?.updatedBy ?? "Unavailable"}
                          />
                        </Stack>
                      </details>
                    </Stack>
                  </Card>
                  {isDetailLoading ? (
                    <Text tone="subtle">Loading asset details…</Text>
                  ) : null}
                  {(detail?.files.length ?? 0) > 0 ? (
                    <Stack gap={12}>
                      <SectionHeader title="Files" />
                      {detail?.files.map((file) => (
                        <ListCard disabled key={file.id}>
                          <Group align="center" gap={8}>
                            <Icon name="document" size="sm" />
                            <Text size="sm">{file.name}</Text>
                            <Caption tone="subtle">{file.type ?? ""}</Caption>
                          </Group>
                        </ListCard>
                      ))}
                    </Stack>
                  ) : null}
                  {parents.length > 0 ? (
                    <Stack gap={12}>
                      <SectionHeader title="Relationships" />
                      {parents.map((parent) => (
                        <ListCard
                          key={parent.assetId}
                          onClick={() => onSelectSubAsset?.(parent.assetId)}
                        >
                          <Group align="center" justify="between">
                            <Text size="sm">
                              ↑ Parent asset · {parent.name}
                            </Text>
                            <Text size="sm" tone="subtle">
                              {parent.code}
                            </Text>
                          </Group>
                        </ListCard>
                      ))}
                    </Stack>
                  ) : null}
                  {children.length > 0 || subAssets.length > 0 ? (
                    <Stack gap={12}>
                      <SectionHeader
                        title={`Sub-assets (${children.length || subAssets.length})`}
                      />
                      {(children.length ? children : subAssets).map((child) => (
                        <ListCard
                          key={child.assetId}
                          onClick={() => onSelectSubAsset?.(child.assetId)}
                        >
                          <Group align="center" justify="between" gap={12}>
                            <Text size="sm">{child.name}</Text>
                            <Text size="sm" tone="subtle">
                              {child.code}
                            </Text>
                          </Group>
                        </ListCard>
                      ))}
                    </Stack>
                  ) : null}
                  {detail &&
                  detail.files.length === 0 &&
                  children.length === 0 &&
                  subAssets.length === 0 &&
                  parents.length === 0 ? (
                    <Text tone="subtle">
                      No linked files or relationships are available.
                    </Text>
                  ) : null}
                </Stack>
              </div>
            </TabsContent>
            <TabsContent value="history">
              <div className="pt-(--spacing-20)">
                {detail?.history.length ? (
                  <Stack gap={12}>
                    {detail.history.map((event) => (
                      <Group align="start" gap={12} key={event.id}>
                        <Icon name="clock-seven" size="sm" />
                        <Stack gap={4}>
                          <Text size="sm">{event.label}</Text>
                          <Caption tone="subtle">
                            {displayDate(event.occurredAt)}
                            {event.actor ? ` · ${event.actor}` : ""}
                          </Caption>
                        </Stack>
                      </Group>
                    ))}
                  </Stack>
                ) : (
                  <Text tone="subtle">
                    No status history is available for this asset.
                  </Text>
                )}
              </div>
            </TabsContent>
          </DrawerBody>
        </Tabs>
        <DrawerFooter>
          {onStartWorkOrder ? (
            <Button fullWidth onClick={onStartWorkOrder} variant="primary">
              Start Work Order
            </Button>
          ) : null}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
